/* ============================================
   WISHLIST FEATURE
   localStorage-based wishlist with custom elements
   ============================================ */

const WISHLIST_STORAGE_KEY = 'theme:wishlist';

/* --- WishlistManager (static utility class) --- */

class WishlistManager {
  static getAll() {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  }

  static has(handle) {
    return WishlistManager.getAll().includes(handle);
  }

  static add(handle) {
    const items = WishlistManager.getAll();
    if (!items.includes(handle)) {
      items.unshift(handle);
      localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(items));
      WishlistManager._dispatch();
      return true;
    }
    return false;
  }

  static remove(handle) {
    const items = WishlistManager.getAll();
    const index = items.indexOf(handle);
    if (index > -1) {
      items.splice(index, 1);
      localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(items));
      WishlistManager._dispatch();
      return true;
    }
    return false;
  }

  static toggle(handle) {
    if (WishlistManager.has(handle)) {
      WishlistManager.remove(handle);
      return false;
    } else {
      WishlistManager.add(handle);
      return true;
    }
  }

  static count() {
    return WishlistManager.getAll().length;
  }

  static _dispatch() {
    document.dispatchEvent(new CustomEvent('wishlist:change', {
      detail: { items: WishlistManager.getAll() }
    }));
  }
}

// Cross-tab sync
window.addEventListener('storage', function(e) {
  if (e.key === WISHLIST_STORAGE_KEY) {
    document.dispatchEvent(new CustomEvent('wishlist:change', {
      detail: { items: WishlistManager.getAll() }
    }));
  }
});

/* --- <wishlist-button> Custom Element --- */

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this._handle = this.getAttribute('handle');
    this._button = this.querySelector('button');
    this._outlineIcon = this.querySelector('.wishlist-button__icon--outline');
    this._filledIcon = this.querySelector('.wishlist-button__icon--filled');
    this._labelAdd = this.querySelector('.wishlist-button__label--add');
    this._labelRemove = this.querySelector('.wishlist-button__label--remove');

    this._syncState();

    this._button.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const added = WishlistManager.toggle(this._handle);
      const message = added ? 'Added to wishlist' : 'Removed from wishlist';
      document.dispatchEvent(new CustomEvent('toast:show', {
        detail: { message: message, duration: 2000 }
      }));
    });

    document.addEventListener('wishlist:change', () => this._syncState());
  }

  _syncState() {
    const isInWishlist = WishlistManager.has(this._handle);
    this._outlineIcon.hidden = isInWishlist;
    this._filledIcon.hidden = !isInWishlist;
    this._button.setAttribute('aria-pressed', isInWishlist.toString());
    this.classList.toggle('is-active', isInWishlist);
    if (this._labelAdd) this._labelAdd.hidden = isInWishlist;
    if (this._labelRemove) this._labelRemove.hidden = !isInWishlist;
  }
}

if (!window.customElements.get('wishlist-button')) {
  window.customElements.define('wishlist-button', WishlistButton);
}

/* --- <wishlist-count> Custom Element --- */

class WishlistCount extends HTMLElement {
  connectedCallback() {
    this._sync();
    document.addEventListener('wishlist:change', () => this._sync());
  }

  _sync() {
    const count = WishlistManager.count();
    this.hidden = count === 0;
    const srSpan = this.querySelector('.sr-only');
    if (srSpan) {
      srSpan.textContent = count + ' item' + (count !== 1 ? 's' : '') + ' in wishlist';
    }
  }
}

if (!window.customElements.get('wishlist-count')) {
  window.customElements.define('wishlist-count', WishlistCount);
}

/* --- <wishlist-page> Custom Element --- */

class WishlistPage extends HTMLElement {
  connectedCallback() {
    this._loadProducts();
    document.addEventListener('wishlist:change', () => {
      this._loaded = false;
      this._loadProducts();
    });
  }

  async _loadProducts() {
    if (this._loaded) return;
    this._loaded = true;

    const handles = WishlistManager.getAll().slice(0, 50);
    const section = this.closest('.shopify-section');
    const sectionId = this._extractSectionId(section);

    let queryString;
    if (handles.length === 0) {
      queryString = 'handle:__nonexistent__';
    } else {
      queryString = handles.map(function(h) { return 'handle:' + h; }).join(' OR ');
    }

    const url = Shopify.routes.root + 'search?type=product&q=' + encodeURIComponent(queryString) + '&section_id=' + sectionId;

    try {
      const response = await fetch(url, { priority: 'low' });
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const newContent = html.querySelector('wishlist-page');

      if (newContent) {
        this.replaceChildren.apply(this, Array.from(document.importNode(newContent, true).childNodes));
      }
    } catch (e) {
      // Silently fail on network errors
    }
  }

  _extractSectionId(element) {
    var sectionElement = element.closest('[id^="shopify-section-"]');
    if (sectionElement) {
      return sectionElement.id.replace('shopify-section-', '');
    }
    return '';
  }
}

if (!window.customElements.get('wishlist-page')) {
  window.customElements.define('wishlist-page', WishlistPage);
}
